<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import {
  Language,
  StepProposalResult,
  StepProposalsFromMediaMutation,
  StepProposalsFromMediaMutationVariables,
} from '@/generated/graphql'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { computed, reactive, ref } from 'vue'
import { v4 } from 'uuid'
import LanguagePicker from '@/components/input/LanguagePicker.vue'
import SelectField from '@/components/input/SelectField.vue'
import DurationField from '@/components/input/DurationField.vue'
import TextAreaField from '@/components/input/TextAreaField.vue'
import { PickedUpload } from '@/components/upload/UploadPicker.vue'
import StepProposalsProgress from '@/components/step/StepProposalsProgress.vue'
import useVuelidate from '@vuelidate/core'
import { required } from '@/validation'
import { stepProposalFragments } from '@/components/step/graphql'

const props = defineProps<{
  upload: PickedUpload
}>()
const emit = defineEmits<{
  result: [value: StepProposalResult]
}>()
const { t } = useI18n()

type OutputLanguageOption = Language | 'SAME_AS_INPUT'

type Model = {
  language?: Language
  outputLanguage?: OutputLanguageOption
  averageStepDurationInSeconds?: number
  additionalPrompt?: string
}
const model = reactive<Model>({
  outputLanguage: 'SAME_AS_INPUT',
  averageStepDurationInSeconds: 60,
  additionalPrompt: '',
})
const validation = useVuelidate<Model>(
  {
    language: { required },
  },
  model,
)

const outputLanguageItems = computed(() => [
  {
    title: t('component.stepProposalsFromMediaLoader.outputLanguageSameAsInput'),
    value: 'SAME_AS_INPUT' as const,
  },
  ...Object.values(Language).map((l) => ({
    title: t(`language.${l}`),
    value: l,
  })),
])

const effectiveOutputLanguage = computed(() => {
  if (model.outputLanguage === 'SAME_AS_INPUT' || !model.outputLanguage) {
    return model.language
  }
  return model.outputLanguage
})

const mutation = useMutation<
  StepProposalsFromMediaMutation,
  StepProposalsFromMediaMutationVariables
>(gql`
  mutation StepProposalsFromMedia($command: CreateStepProposalsFromMedia!) {
    product {
      createStepProposalsFromMedia(command: $command) {
        ...StepProposalResultDetails
      }
    }
  }

  ${stepProposalFragments}
`)

const loadingRequestId = ref<string>()
const doProposals = () => {
  validation.value.$touch()
  if (validation.value.$invalid) {
    return
  }

  loadingRequestId.value = v4()
  mutation
    .mutate({
      command: {
        id: loadingRequestId.value as string,
        fromUploadId: props.upload.id,
        inLanguage: model.language as Language,
        outLanguage: effectiveOutputLanguage.value,
        averageStepSizeInSeconds: model.averageStepDurationInSeconds,
        additionalPrompt: model.additionalPrompt?.trim() || undefined,
      },
    })
    .then((response) => {
      emit('result', response?.data?.product.createStepProposalsFromMedia as StepProposalResult)
    })
    .catch(() => {
      setTimeout(() => (loadingRequestId.value = undefined), 3_000)
    })
}
</script>

<template>
  <template v-if="!loadingRequestId">
    <language-picker
      v-model="model.language"
      :validation="validation.language"
      :label="t('component.stepProposalsFromMediaLoader.inputLanguage')"
    />
    <select-field
      v-model="model.outputLanguage"
      :items="outputLanguageItems"
      :label="t('component.stepProposalsFromMediaLoader.outputLanguage')"
    />
    <duration-field
      v-model="model.averageStepDurationInSeconds"
      :label="t('component.stepProposalsFromMediaLoader.averageStepDuration')"
      :hint="t('component.stepProposalsFromMediaLoader.averageStepDurationHint')"
    />
    <text-area-field
      v-model="model.additionalPrompt"
      :label="t('component.stepProposalsFromMediaLoader.additionalPrompt')"
      :hint="t('component.stepProposalsFromMediaLoader.additionalPromptHint')"
      rows="3"
    />
    <div class="text-center">
      <v-btn color="primary" class="mt-5" :loading="mutation.loading.value" @click="doProposals">
        {{ t('component.stepProposalsFromFileDialog.buttonCreateProposals') }}
      </v-btn>
    </div>
  </template>

  <step-proposals-progress v-if="!!loadingRequestId" :id="loadingRequestId" />
</template>

<style scoped lang="scss"></style>
